import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getReferrerCode, getPartnerSlug } from "@/lib/useReferralTracker";
import { BACKEND_URL } from "@/lib/api";

// Dev sign-in is on whenever REACT_APP_DEV_LOGIN_KEY is set in the
// frontend env. The actual key check happens server-side — this just
// gates the form's visibility so prod builds never show it.
const DEV_LOGIN_KEY = (process.env.REACT_APP_DEV_LOGIN_KEY || "").trim();

function DevLoginCard() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState(process.env.REACT_APP_DEV_LOGIN_EMAIL || "");
  const [key, setKey] = useState(DEV_LOGIN_KEY);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  async function submit(e) {
    e?.preventDefault();
    if (!email || !key) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/dev-login", { email, key });
      setUser(data.user);
      const u = data?.user;
      const dest = u && !u.is_admin && u.role === "deals_user"
        ? "/admin/deals"
        : "/dashboard";
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "dev login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-6 p-4 border border-dashed border-amber-300 bg-amber-50/60 rounded-[10px] space-y-2.5" data-testid="dev-login-card">
      <div className="text-[10.5px] uppercase tracking-[0.12em] text-amber-800 font-semibold">Local dev sign-in</div>
      <input
        className="input"
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="off"
        required
        data-testid="dev-login-email"
      />
      <input
        className="input"
        type="password"
        placeholder="DEV_LOGIN_KEY"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        autoComplete="off"
        required
        data-testid="dev-login-key"
      />
      {err && (
        <div className="text-[12px] p-2 rounded-[8px]" style={{ background: "var(--bad-bg)", color: "var(--bad)" }}>{err}</div>
      )}
      <button
        type="submit"
        disabled={busy || !email || !key}
        className="btn btn-primary text-[13px] w-full"
        data-testid="dev-login-submit"
      >
        {busy ? <><span className="spinner" /> Signing in…</> : "Sign in (dev)"}
      </button>
      <div className="text-[10.5px] text-amber-800/80 leading-relaxed">
        Only visible when <code className="font-mono">REACT_APP_DEV_LOGIN_KEY</code> is set in <code className="font-mono">frontend/.env</code>. The backend rejects the call unless <code className="font-mono">DEV_LOGIN_KEY</code> is set there too.
      </div>
    </form>
  );
}

function InvitedByBanner({ inviterName }) {
  return (
    <div
      className="mb-5 flex items-start gap-3 border border-emerald-200 bg-emerald-50/70 rounded-[10px] px-3.5 py-3"
      data-testid="invited-by-banner"
    >
      <div className="mt-0.5 w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
        <Sparkles size={14} className="text-emerald-700" />
      </div>
      <div className="flex-1 leading-relaxed">
        <div className="text-[13.5px] font-semibold text-emerald-900">
          You've been invited by {inviterName}
        </div>
        <div className="text-[12.5px] text-emerald-800 mt-0.5">
          Sign in with Google below to accept the invitation and create your
          Reservoir account.
        </div>
      </div>
    </div>
  );
}

function PartnerInvitedBanner({ partner }) {
  return (
    <div
      className="mb-5 flex items-center gap-3 border border-emerald-200 bg-emerald-50/70 rounded-[10px] px-3.5 py-3"
      data-testid="login-partner-banner"
    >
      <img
        src={`${BACKEND_URL}${partner.logo_url}`}
        alt={`${partner.name} logo`}
        className="h-8 w-auto max-w-[80px] object-contain shrink-0"
      />
      <div className="flex-1 min-w-0 leading-relaxed">
        <div className="text-[13.5px] font-semibold text-emerald-900 truncate">
          Invited by {partner.name}
        </div>
        <div className="text-[12.5px] text-emerald-800 mt-0.5">
          Sign in with Google below to create your Reservoir account.
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const [inviter, setInviter] = useState(null);
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    const code = getReferrerCode();
    if (!code) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/referrals/inviter/${encodeURIComponent(code)}`);
        if (!cancelled && data?.name) setInviter({ name: data.name });
      } catch {
        /* invalid or stale code — silently skip the banner */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const slug = getPartnerSlug();
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/partners/by-slug/${encodeURIComponent(slug)}`);
        if (!cancelled && data?.name) setPartner(data);
      } catch {
        /* invalid or archived slug — skip silently */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-10 relative overflow-hidden"
           style={{
             background:
               "linear-gradient(180deg, #0a0a0a 0%, #171717 60%, #262626 100%)",
             color: "#fafaf7",
           }}>
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="login-brand-link">
          <div className="w-9 h-9 rounded-lg bg-white/95 flex items-center justify-center text-black font-bold font-display">
            R
          </div>
          <span className="font-display font-semibold tracking-tight">
            Reservoir <span className="text-neutral-500 font-medium">AI</span>
          </span>
        </Link>

        <div>
          <div className="section-eyebrow" style={{ color: "#a3a3a3" }}>
            Points · Miles · Credits
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold mt-3 max-w-md leading-tight">
            Stop leaving points on the table.
          </h1>
          <p className="text-neutral-400 mt-4 max-w-md text-[15px] leading-relaxed">
            Reservoir watches every loyalty currency you own — hotels,
            airlines, transferable cards — and a points-obsessed Concierge
            tells you exactly when and how to use them.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: "498k", v: "Transferable" },
              { k: "475k", v: "Hotel pts" },
              { k: "$750", v: "Cash & credits" },
            ].map((s, i) => (
              <div
                key={i}
                className="border border-white/10 rounded-[12px] p-3 rise-in"
                style={{ animationDelay: `${100 + i * 80}ms` }}
              >
                <div className="font-display tabular text-xl font-bold">{s.k}</div>
                <div className="text-xs text-neutral-400 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-neutral-500">
          Trusted by points-and-miles enthusiasts who hate spreadsheets.
        </div>
      </div>

      {/* Right: auth panel */}
      <div className="flex-1 flex items-center justify-center p-6 fade-in">
        <div className="card max-w-md w-full p-8 lg:p-10" data-testid="login-card">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity" data-testid="login-brand-link-mobile">
            <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center text-white font-bold font-display">
              R
            </div>
            <span className="font-display font-semibold tracking-tight">
              Reservoir <span className="text-neutral-400 font-medium">AI</span>
            </span>
          </Link>

          {partner ? (
            <>
              <PartnerInvitedBanner partner={partner} />
              <div className="section-eyebrow">Claim your invitation</div>
              <h2 className="font-display text-3xl font-extrabold mt-2">Sign in</h2>
              <p className="text-neutral-600 text-[14px] mt-2">
                Continue with Google to accept {partner.name}'s invitation.
                You'll land on a clean dashboard — add the loyalty programs
                you use to start planning award trips.
              </p>
            </>
          ) : inviter ? (
            <>
              <InvitedByBanner inviterName={inviter.name} />
              <div className="section-eyebrow">Claim your invitation</div>
              <h2 className="font-display text-3xl font-extrabold mt-2">Sign in</h2>
              <p className="text-neutral-600 text-[14px] mt-2">
                Continue with Google to accept {inviter.name}'s invitation. You'll
                land on a clean dashboard — add the loyalty programs you use,
                and {inviter.name} will be credited with the referral.
              </p>
            </>
          ) : (
            <>
              <div className="section-eyebrow">Welcome</div>
              <h2 className="font-display text-3xl font-extrabold mt-2">Sign in</h2>
              <p className="text-neutral-600 text-[14px] mt-2">
                Continue with Google. You'll land on a clean dashboard — add the
                programs you actually use, one at a time.
              </p>
            </>
          )}

          <div className="mt-6">
            <GoogleSignInButton />
          </div>

          {DEV_LOGIN_KEY && <DevLoginCard />}

          <div className="mt-6 pt-6 border-t border-black/5 text-xs text-neutral-500 leading-relaxed">
            By continuing you agree to the points-and-miles tracking handshake.
            We never see your card numbers — only the balances and tier
            statuses you choose to add yourself.
          </div>
        </div>
      </div>
    </div>
  );
}
